import { faChevronDown, faChevronUp, faDownload, faCircleXmark, faUpload, faExclamationTriangle, faSpinner, faClock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useRef } from 'react'
import { ImageConfig } from './ImageConfig'

import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'
import moment from 'moment'
import '../viewRequest.css'


const OHViewRequest = ({ viewingRequest, onUpdate, onClose }) => {
    const axios = useAxiosPrivate()

    const { setShowLoader, createInfo, auth } = useAuth()

    const [activityLogItems, setActivityLogItems] = useState(null)
    const [isAccordionOpen, setAccordionOpen] = useState(false)
    const [isFormResponsesOpen, setFormResponsesOpen] = useState(false)
    const [isDocumentsOpen, setDocumentsOpen] = useState(false)
    const [isSupportingDocumentsOpen, setSupportingDocumentsOpen] = useState(false)
    const [requestItems, setRequestItems] = useState(null)
    const [chosenAssessmentFile, setChosenAssessmentFile] = useState(null)

    const [supportingDocuments, setSupportingDocuments] = useState([]);
    const [uploading, setUploading] = useState(false)

    const wrapperRef = useRef(null);
    const [fileList, setFileList] = useState([]);
    const [errorMsg, setErrorMsg] = useState(false);

    const [sessionHistoryOpen, setSessionHistoryOpen] = useState(false)

    useEffect(() => {
        if (viewingRequest?.activity_log) {
            const activityLog =
                typeof viewingRequest.activity_log === 'string'
                    ? JSON.parse(viewingRequest.activity_log)
                    : viewingRequest.activity_log

            setActivityLogItems(activityLog)
        }

        if (viewingRequest?.request_info_array) {
            setRequestItems(viewingRequest.request_info_array)
        } else if (viewingRequest?.request_information) {
            const requestInfo =
                typeof viewingRequest.request_information === 'string'
                    ? JSON.parse(viewingRequest.request_information)
                    : viewingRequest.request_information

            setRequestItems(requestInfo)
        }

        setSupportingDocuments(viewingRequest?.supporting_documents)
    }, [viewingRequest])

    const toggleAccordion = () => {
        setAccordionOpen(!isAccordionOpen)
        setFormResponsesOpen(false)
        setDocumentsOpen(false)
        setSupportingDocumentsOpen(false)
        setSessionHistoryOpen(false)
    }

    const toggleFormResponses = () => {
        setFormResponsesOpen(!isFormResponsesOpen)
        setAccordionOpen(false)
        setDocumentsOpen(false)
        setSupportingDocumentsOpen(false)
        setSessionHistoryOpen(false)
    }

    const toggleSessionHistory = () => {
        setSessionHistoryOpen(!sessionHistoryOpen)
        setAccordionOpen(false)
        setDocumentsOpen(false)
        setSupportingDocumentsOpen(false)
        setFormResponsesOpen(false)
    }

    const toggleSupportingDocuments = () => {
        setSupportingDocumentsOpen(!isSupportingDocumentsOpen)
        setAccordionOpen(false)
        setFormResponsesOpen(false)
        setDocumentsOpen(false)
        setSessionHistoryOpen(false)
    }

    const getStatusStyle = (status) => {
        const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'

        if (typeof status !== 'string') {
            return `${baseClasses} bg-gray-200 text-gray-800`
        }

        switch (status.toLowerCase()) {
            case 'draft':
            case 'awaiting submission':
            case 'awaiting review':
            case 'awaiting confirmation of appointment':
                return `${baseClasses} bg-blue-200 text-blue-800 border-blue-400`
            case 'awaiting consent':
            case 'awaiting assessment':
            case 'awaiting clinical review':
            case 'appointment confirmed':
            case 'appointment attended':
            case 'recommendations need approval':
                return `${baseClasses} bg-amber-200 text-amber-800 border-amber-400`
            case 'consent declined':
            case 'cancelled':
            case 'withdrawn consent':
                return `${baseClasses} bg-red-200 text-red-800 border-red-400`
            case 'completed':
            case 'report ready':
            case 'closed':
                return `${baseClasses} bg-green-200 text-green-800 border-green-400`
            case 'dna/unable to proceed':
                return `${baseClasses} bg-gray-200 text-gray-800 border-gray-400`
            default:
                return `${baseClasses} bg-gray-200 text-gray-800`
        }
    }

    const isDraft = (viewingRequest?.request_status || '').toLowerCase() === 'draft'

    const submitDraft = async () => {
        const confirmed = window.confirm(
            'This will submit the draft referral and start the referral process. Do you want to continue?'
        )
        if (!confirmed) return

        setShowLoader(true)
        try {
            const response = await axios.post(`/service-requests/oh/submit/${viewingRequest.id}`)
            if (response.status === 200) {
                createInfo('success', 'Draft submitted successfully')
                onUpdate && onUpdate()
                onClose && onClose()
            } else {
                createInfo('error', 'Failed to submit draft')
            }
        } catch (error) {
            console.error('Error submitting draft:', error)
            createInfo('error', 'Failed to submit draft')
        } finally {
            setShowLoader(false)
        }
    }

    const cancelDraft = async () => {
        const confirmed = window.confirm(
            'Cancel this draft referral? This will mark it as cancelled.'
        )
        if (!confirmed) return

        setShowLoader(true)
        try {
            const response = await axios.post(`/service-requests/oh/cancel-draft/${viewingRequest.id}`)
            if (response.status === 200) {
                createInfo('success', 'Draft cancelled successfully')
                onUpdate && onUpdate()
                onClose && onClose()
            } else {
                createInfo('error', 'Failed to cancel draft')
            }
        } catch (error) {
            console.error('Error cancelling draft:', error)
            createInfo('error', 'Failed to cancel draft')
        } finally {
            setShowLoader(false)
        }
    }

    const downloadFile = async (fileName, type = 'supporting') => {
        try {
            let downloadUrl;

            if (type === 'result') {
                const cleanFileName = fileName.replace('/pdf/oh-assessment/', '');
                downloadUrl = `${process.env.REACT_APP_KIS}/download-decrypted-file/${cleanFileName}`;
            } else {
                downloadUrl = `${process.env.REACT_APP_KIS}/download-supporting-document/${viewingRequest?.service_request_id}/${fileName}`;
            }

            window.open(downloadUrl, '_blank').focus();
        } catch (error) {
            console.error('Error downloading the document', error);
        }
    };

    const downloadReport = async () => {
        try {
            let cleanFileName
            if (chosenAssessmentFile === 'Original') {
                console.log(requestItems)
                cleanFileName = requestItems.filename.replace('/pdf/oh-assessment/', '');
            } else {
                cleanFileName = viewingRequest?.request_info_array?.extension_data[chosenAssessmentFile]?.filename.replace('/pdf/oh-assessment/', '')
            }
            const downloadUrl = `${process.env.REACT_APP_KIS}/download-decrypted-file/${cleanFileName}`;
            window.open(downloadUrl, '_blank').focus();
        } catch (error) {
            console.error('Error downloading the document', error);
        }
    }
    
    // File upload functions
    const allowedFileTypes = [
        '.doc',
        '.docx',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.xls',
        '.xlsx',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        '.csv',
        'application/pdf',
        '.pdf',
        'image/jpeg',
        '.jpg',
        'image/png',
        '.png',
        'image/tiff',
        '.tiff',
    ];

    const isValidFileType = (file) => {
        const extension = `.${file.name.split('.').pop()}`;
        const mimeType = file.type;
        return (
            allowedFileTypes.includes(extension) ||
            allowedFileTypes.includes(mimeType)
        );
    };

    const onDragEnter = () => wrapperRef.current.classList.add('dragover');
    const onDragLeave = () => wrapperRef.current.classList.remove('dragover');
    const onDragOver = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };
    const onDrop = (e) => {
        e.preventDefault();
        wrapperRef.current.classList.remove('dragover');
        onFileDrop(e.nativeEvent.dataTransfer.files);
    };

    const onFileUpload = async (e) => {
        e.preventDefault();
        const newFile = e.target.files[0];

        if (newFile && isValidFileType(newFile)) {
            const updatedList = [...fileList, newFile];
            setFileList(updatedList);
        } else {
            console.error('Invalid file type. Please select a valid file.');
        }
    };

    const onFileDrop = async (files) => {
        const newFile = files[0];

        if (newFile && isValidFileType(newFile)) {
            const updatedList = [...fileList, newFile];
            setFileList(updatedList);
        } else {
            createInfo('error', 'Invalid file type');
            return;
        }
    };

    const fileRemove = (file) => {
        const updatedList = [...fileList];
        updatedList.splice(fileList.indexOf(file), 1);
        setFileList(updatedList);
    };

    const uploadDocuments = async () => {
        if (fileList.length === 0) {
            createInfo('error', 'Please select files to upload');
            return;
        }

        setUploading(true);
        const data = new FormData();

        for (let i = 0; i < fileList.length; i++) {
            data.append('file[]', fileList[i], fileList[i].name);
        }

        // Add current user information
        if (auth?.user?.id) {
            data.append('userId', auth?.user?.contact_id);
        }
        if (auth?.user?.name) {
            data.append('userName', auth?.user?.contact_name);
        }

        try {
            const response = await axios.post(`/upload-supporting-document/${viewingRequest?.service_request_id}`, data,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.status !== 200) {
                createInfo('error', 'Failed to upload documents');
                return;
            }

            setFileList([]);
            createInfo('success', 'Document Successfully Uploaded');

            // Call onUpdate to refresh the parent component data
            if (onUpdate) {
                onUpdate();
            }
        } catch (error) {
            console.error('File upload error:', error);
            createInfo('error', 'Failed to upload documents');
        } finally {
            setUploading(false);
        }
    }

    const downloadCaseFile = async () => {
        try {
            const downloadUrl = `${process.env.REACT_APP_KIS}/download-case-file/${viewingRequest?.service_request_id}`;
            window.open(downloadUrl, '_blank');
        } catch (error) {
            console.error('File download error:', error);
            createInfo('error', 'Failed to download case file');
        }
    }

    const renderLegacyFormResponses = () => {
        if (!requestItems?.form_responses) return null

        const responses = typeof requestItems.form_responses === 'string'
            ? JSON.parse(requestItems.form_responses)
            : requestItems.form_responses

        return (
            <div className="space-y-6">
                {responses.question1 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-3 text-base">{responses.question1.question_title || 'Question 1: Support Reason'}</h4>
                        <div className="bg-gray-50 p-3 rounded-md space-y-2 text-sm">
                            {responses.question1.responses && Object.entries(responses.question1.responses).map(([key, item]) => (
                                <div key={key} className="flex">
                                    <span className="font-medium text-gray-700 min-w-0 flex-shrink-0 w-1/3">{item.label}:</span>
                                    <span className="text-gray-900 ml-2 flex-1">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {responses.question2 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-3 text-base">{responses.question2.question_title || 'Question 2: Issue Nature'}</h4>
                        <div className="bg-gray-50 p-3 rounded-md space-y-2 text-sm">
                            {responses.question2.responses && Object.entries(responses.question2.responses).map(([key, item]) => (
                                <div key={key} className="flex">
                                    <span className="font-medium text-gray-700 min-w-0 flex-shrink-0 w-1/3">{item.label}:</span>
                                    <span className="text-gray-900 ml-2 flex-1">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {responses.question3 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-3 text-base">{responses.question3.question_title || 'Question 3: Meetings and Outcomes'}</h4>
                        <div className="bg-gray-50 p-3 rounded-md space-y-2 text-sm">
                            {responses.question3.responses && Object.entries(responses.question3.responses).map(([key, item]) => (
                                <div key={key} className="flex">
                                    <span className="font-medium text-gray-700 min-w-0 flex-shrink-0 w-1/3">{item.label}:</span>
                                    <span className="text-gray-900 ml-2 flex-1">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {responses.question4 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-3 text-base">{responses.question4.question_title || 'Question 4: Actions Implemented'}</h4>
                        <div className="bg-gray-50 p-3 rounded-md space-y-2 text-sm">
                            {responses.question4.responses && Object.entries(responses.question4.responses).map(([key, item]) => (
                                <div key={key} className="flex">
                                    <span className="font-medium text-gray-700 min-w-0 flex-shrink-0 w-1/3">{item.label}:</span>
                                    <span className="text-gray-900 ml-2 flex-1">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {responses.question5 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-3 text-base">{responses.question5.question_title || 'Question 5: Intended Outcome'}</h4>
                        <div className="bg-gray-50 p-3 rounded-md space-y-2 text-sm">
                            {responses.question5.responses && Object.entries(responses.question5.responses).map(([key, item]) => (
                                <div key={key} className="flex">
                                    <span className="font-medium text-gray-700 min-w-0 flex-shrink-0 w-1/3">{item.label}:</span>
                                    <span className="text-gray-900 ml-2 flex-1">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {responses.question6 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-3 text-base">{responses.question6.question_title || 'Question 6: Additional Information'}</h4>
                        <div className="bg-gray-50 p-3 rounded-md space-y-2 text-sm">
                            {responses.question6.responses && Object.entries(responses.question6.responses).map(([key, item]) => (
                                <div key={key} className="flex">
                                    <span className="font-medium text-gray-700 min-w-0 flex-shrink-0 w-1/3">{item.label}:</span>
                                    <span className="text-gray-900 ml-2 flex-1">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const renderFormResponses = () => {
        if (!requestItems?.form_responses) return null

        const responses = typeof requestItems.form_responses === 'string'
            ? JSON.parse(requestItems.form_responses)
            : requestItems.form_responses

        const renderResponseItem = (item, depth = 0) => {
            const indentClass = depth > 0 ? `ml-${depth * 4}` : ''
            const bgClass = depth % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'

            return (
                <div key={`${item.answer_text}-${depth}`} className={`${indentClass} ${bgClass} px-1 rounded-md`}>
                    <div className="flex flex-col">
                        <span className="font-medium text-gray-800">{item.answer_text}</span>

                        {item.text_response && (
                            <div className="mt-1 text-sm text-gray-600 italic">
                                {item.text_response}
                            </div>
                        )}

                        {item.duration && (
                            <div className="mt-1 text-sm text-gray-600">
                                <span className="font-medium">Duration:</span> {item.duration}
                            </div>
                        )}
                    </div>

                    {item.sub_responses && item.sub_responses.length > 0 && (
                        <div className="mt-1 space-y-1">
                            {item.sub_responses.map((subItem, index) => renderResponseItem(subItem, depth + 1))}
                        </div>
                    )}
                </div>
            )
        }

        const renderQuestionResponses = (responses, questionTitle) => {
            if (!responses || responses.length === 0) return null

            if (Array.isArray(responses[0])) {
                return (
                    <div className="space-y-2">
                        {responses.map((responseGroup, groupIndex) => (
                            <div key={groupIndex} className=" rounded-md p-1">
                                <div className="text-sm font-medium text-gray-600 mb-1">
                                    {groupIndex === 0 ? 'Meetings held:' : 'Outcomes:'}
                                </div>
                                {responseGroup.map((item, itemIndex) => renderResponseItem(item, 0))}
                            </div>
                        ))}
                    </div>
                )
            }

            return (
                <div className="space-y-1">
                    {responses.map((item, index) => renderResponseItem(item, 0))}
                </div>
            )
        }

        return (
            <div className="space-y-3">
                {responses.question1 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-1 text-base">{responses.question1.question_title || 'Question 1: Support Reason'}</h4>
                        <div className="rounded-md p-1">
                            {renderQuestionResponses(responses.question1.responses)}
                        </div>
                    </div>
                )}

                {responses.question2 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-1 text-base">{responses.question2.question_title || 'Question 2: Issue Nature'}</h4>
                        <div className="rounded-md p-1">
                            {renderQuestionResponses(responses.question2.responses)}
                        </div>
                    </div>
                )}

                {responses.question3 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-1 text-base">{responses.question3.question_title || 'Question 3: Meetings and Outcomes'}</h4>
                        <div className="rounded-md p-1">
                            {renderQuestionResponses(responses.question3.responses)}
                        </div>
                    </div>
                )}

                {responses.question4 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-1 text-base">{responses.question4.question_title || 'Question 4: Actions Implemented'}</h4>
                        <div className="rounded-md p-1">
                            {renderQuestionResponses(responses.question4.responses)}
                        </div>
                    </div>
                )}

                {responses.question5 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-1 text-base">{responses.question5.question_title || 'Question 5: Intended Outcome'}</h4>
                        <div className="rounded-md p-1">
                            {renderQuestionResponses(responses.question5.responses)}
                        </div>
                    </div>
                )}

                {responses.question6 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-1 text-base">{responses.question6.question_title || 'Question 6: Employee Questions'}</h4>
                        <div className="rounded-md p-1">
                            {renderQuestionResponses(responses.question6.responses)}
                        </div>
                    </div>
                )}

                {responses.question7 && (
                    <div className="border-l-4 border-gray-300 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-1 text-base">{responses.question7.question_title || 'Question 7: Additional Information'}</h4>
                        <div className="rounded-md p-1">
                            {responses.question7.responses && responses.question7.responses.length > 0 && responses.question7.responses[0].text_response && (
                                <div className="text-gray-800">
                                    {responses.question7.responses[0].text_response ?? 'N/A'}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {requestItems?.active_session && (
                <div class={`border rounded-lg shadow-md mb-4 ${requestItems?.active_session?.cancelled ? 'bg-amber-200 border-amber-800' : 'bg-green-100 border-green-500'}`}>
                    <h3 class="font-medium text-gray-900 pl-4 mb-1 mt-1 pt-2">Assessment Information {requestItems?.active_session?.cancelled ? (requestItems?.active_session?.cancel_reason_id === 1 ? '(Did Not Attend)' : '(Cancelled)') : ''}</h3>
                    <div class="grid grid-cols-2 gap-6 mb-2 text-sm pl-4 pt-2 pb-2">
                        <div>
                            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date</div>
                            <div class="font-mono text-gray-900">{moment(requestItems?.active_session?.start).format("dddd D MMMM yyyy")}</div>
                        </div>
                        <div>
                            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Time</div>
                            <div class="font-mono text-gray-900">{moment(requestItems?.active_session?.start).format("HH:mm")} - {moment(requestItems?.active_session?.end).format("HH:mm")}</div>
                        </div>
                    </div>
                </div>
            )}
            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Occupational Health Request
                    </h2>
                    <span className={getStatusStyle(viewingRequest?.request_status)}>
                        {viewingRequest?.request_status || 'Unknown'}
                    </span>
                </div>

                {isDraft && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                        <div className="text-sm text-gray-600">
                            This referral is saved as a draft and has not been submitted.
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={cancelDraft}
                                className="inline-flex items-center rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-700"
                            >
                                Cancel draft
                            </button>
                            <button
                                type="button"
                                onClick={submitDraft}
                                className="inline-flex items-center rounded-md bg-[#02ADAD] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                            >
                                Submit referral
                            </button>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-6 text-sm">
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reference</div>
                        <div className="font-mono text-gray-900">{viewingRequest?.reference || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Type</div>
                        <div className="text-gray-900">Occupational Health Request</div>
                    </div>
                </div>

                {viewingRequest?.description && (
                    <div className="text-sm mt-4">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</div>
                        <div className="text-gray-900">{viewingRequest.description}</div>
                    </div>
                )}
            </div>

            {requestItems?.filename && (['completed', 'report ready'].includes(viewingRequest?.request_status.toLowerCase()) || viewingRequest?.request_info_array?.extensions) && (
                <div className="border-b border-gray-200 pb-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="text-sm text-gray-600">
                            OH Assessment document is ready for download.
                        </div>
                        <div className="flex gap-2">
                            {viewingRequest?.request_info_array?.extensions && (
                                <select
                                    onChange={(e) => setChosenAssessmentFile(e.target.value)}
                                    id="clinic"
                                    className="border-1 border-slate-400 px-2 py-1 text-gray-900 text-sm rounded-lg"
                                >
                                    <option disabled selected>---Please Select---</option>
                                    <option value='Original'>Initial Report</option>
                                    {viewingRequest?.request_info_array?.extension_data?.map((extension, index) => {
                                        const numExt = viewingRequest?.request_info_array?.extensions
                                        return (
                                            (extension?.filename && (['completed', 'report ready'].includes(viewingRequest?.request_status.toLowerCase()) || (index < numExt-1))) && (
                                                <option
                                                    value={index}
                                                >
                                                    Extension {index + 1} Report
                                                </option>
                                            )
                                        )
                                    })}
                                </select>
                            )}
                            <button
                                type="button"
                                onClick={() => chosenAssessmentFile ? downloadReport() : downloadFile(requestItems.filename, 'result')}
                                disabled={viewingRequest?.request_info_array?.extensions && !chosenAssessmentFile}
                                className="inline-flex items-center rounded-md bg-blue-400 px-3 py-2 text-sm font-semibold text-white btn-disal shadow-sm hover:bg-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed"
                            >
                                <FontAwesomeIcon icon={faDownload} className="mr-2 h-4 w-4" />
                                Download OH Assessment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="border-b border-gray-200 pb-4">
                <h3 className="font-medium text-gray-900 mb-3">
                    Staff Details
                </h3>
                <div className="grid grid-cols-3 gap-6 text-sm">
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Name</div>
                        <div className="text-gray-900">
                            {(() => {
                                try {
                                    const requestInfo = typeof viewingRequest?.request_information === 'string'
                                        ? JSON.parse(viewingRequest.request_information)
                                        : viewingRequest?.request_information
                                    return requestInfo?.staff_details?.staff_name || 'N/A'
                                } catch (e) {
                                    return 'N/A'
                                }
                            })()}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date of Birth</div>
                        <div className="text-gray-900">
                            {(() => {
                                try {
                                    const requestInfo = typeof viewingRequest?.request_information === 'string'
                                        ? JSON.parse(viewingRequest.request_information)
                                        : viewingRequest?.request_information
                                    const dob = requestInfo?.staff_details?.date_of_birth
                                    return dob ? moment(dob).format('DD/MM/YYYY') : 'N/A'
                                } catch (e) {
                                    return 'N/A'
                                }
                            })()}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email</div>
                        <div className="text-gray-900 break-all">
                            {(() => {
                                try {
                                    const requestInfo = typeof viewingRequest?.request_information === 'string'
                                        ? JSON.parse(viewingRequest.request_information)
                                        : viewingRequest?.request_information
                                    return requestInfo?.staff_details?.staff_email || 'N/A'
                                } catch (e) {
                                    return 'N/A'
                                }
                            })()}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-b border-gray-200 pb-4">
                <h3 className="font-medium text-gray-900 mb-3">
                    Click Here to Download Case File
                </h3>
                <button className="inline-flex items-center rounded-md bg-blue-400 px-3 py-2 text-sm font-semibold text-white btn-disal shadow-sm hover:bg-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed"
                    onClick={(e) => {e.preventDefault(); downloadCaseFile();} }>
                    <span>
                        <FontAwesomeIcon icon={faDownload} className="mr-2 h-4 w-4" />
                        Download
                    </span>
                </button>
            </div>

            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between">
                    <div
                        className="flex items-center cursor-pointer hover:text-indigo-600 transition-colors select-none"
                        onClick={toggleSupportingDocuments}
                    >
                        <h3 className="font-medium text-gray-900">
                            Supporting Documents
                            {supportingDocuments.length > 0 && (
                                <span className="ml-2 inline-flex items-center pr-2.5 pl-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {supportingDocuments.length}
                                </span>
                            )}
                        </h3>
                        <FontAwesomeIcon
                            icon={isSupportingDocumentsOpen ? faChevronUp : faChevronDown}
                            className="ml-2 h-4 w-4 text-indigo-900"
                        />
                    </div>
                </div>
                {isSupportingDocumentsOpen && (
                    <div className="mb-4">
                        {supportingDocuments && supportingDocuments.length > 0 && (
                            <div className="mb-6" key={`supporting-docs-${supportingDocuments.length}-${JSON.stringify(supportingDocuments.map(d => d.id || d.file_name)).slice(0, 20)}`}>
                                <h4 className="font-semibold text-gray-900 mb-3 text-sm">Uploaded Documents</h4>
                                <div className="space-y-2">
                                    {supportingDocuments.map((doc, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-center">
                                                <img
                                                    src={
                                                        ImageConfig[doc.file_name?.split('.').pop()] ||
                                                        ImageConfig['default']
                                                    }
                                                    alt=""
                                                    className="w-6 h-6"
                                                />
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-gray-900">{doc?.metadata?.original_name}</p>
                                                    <p className="text-xs text-gray-500">
                                                        Uploaded {doc.created_at ? moment(doc.created_at).format('DD/MM/YYYY HH:mm') : ''}
                                                        {doc.uploaded_by && ` by ${doc.uploaded_by}`}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => downloadFile(doc.filename, 'supporting')}
                                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs leading-4 font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            >
                                                <FontAwesomeIcon icon={faDownload} className="mr-1 h-3 w-3" />
                                                Download
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {fileList.length > 0 && (
                            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-start">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 text-sm mt-0.5 mr-2" />
                                    <div className="text-yellow-700 text-xs">
                                        <strong>Files ready for upload:</strong> You have {fileList.length} file{fileList.length > 1 ? 's' : ''} staged for upload. Click the Upload button to submit them.
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="flex items-center justify-between mb-4 text-sm">
                            <h3 className="font-semibold">
                                Upload Documents
                            </h3>
                            <button
                                onClick={uploadDocuments}
                                disabled={uploading || fileList.length === 0}
                                className={`rounded-lg border text-xs px-3 py-1 flex items-center transition-colors ${uploading || fileList.length === 0
                                    ? 'border-gray-300 bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'border-teal-500 bg-teal-500 text-white hover:bg-transparent hover:text-teal-500'
                                    }`}
                            >
                                {uploading ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                    <FontAwesomeIcon icon={faUpload} />
                                )}
                                <span className="pl-2">
                                    {uploading ? 'Uploading...' : 'Upload'}
                                </span>
                            </button>
                        </div>
                        <label
                            htmlFor="file-upload-default"
                            className="block"
                            ref={wrapperRef}
                            onDragEnter={onDragEnter}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                        >
                            <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-4 py-6">
                                <div className="text-center">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="mx-auto h-8 w-8 text-gray-300"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                                        />
                                    </svg>
                                    <div className="mt-2 flex text-xs leading-6 text-gray-600 justify-center">
                                        <div className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500">
                                            <span>Drag and Drop or click to upload</span>
                                            <input
                                                id="file-upload-default"
                                                name="file-upload-default"
                                                type="file"
                                                accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.csv,text/csv,.pdf,application/pdf,image/jpeg,image/png,image/tiff"
                                                multiple
                                                className="sr-only"
                                                onChange={onFileUpload}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs leading-5 text-gray-600">
                                        PNG, JPG, CSV, XLS or PDF up to 10MB
                                    </p>
                                    <p className="error-message">{errorMsg}</p>
                                </div>
                            </div>
                        </label>
                        {fileList.length > 0 && (
                            <div className="mt-4 text-xs">
                                <h4 className="font-semibold mb-2 text-yellow-700 flex items-center">
                                    <FontAwesomeIcon icon={faClock} className="mr-2 text-sm" />
                                    Staged Files ({fileList.length})
                                </h4>
                                <div className="border-2 border-dashed border-yellow-300 bg-yellow-50 rounded p-2">
                                    {fileList.map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-2 mb-2 bg-white border border-yellow-200 rounded shadow-sm"
                                        >
                                            <div className="flex items-center">
                                                <img
                                                    src={
                                                        ImageConfig[item.type.split('/')[1]] ||
                                                        ImageConfig['default']
                                                    }
                                                    alt=""
                                                    className="w-6 h-6"
                                                />
                                                <div className="ml-2">
                                                    <p className="break-words text-gray-700 font-medium">{item.name}</p>
                                                    <p className="text-xs text-gray-500">Ready to upload</p>
                                                </div>
                                            </div>
                                            <span
                                                className="hover:text-red-700 cursor-pointer"
                                                onClick={() => fileRemove(item)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faCircleXmark}
                                                    className="text-gray-700 hover:text-red-700 w-4 h-4"
                                                />
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between">
                    <div
                        className="flex items-center cursor-pointer hover:text-indigo-600 transition-colors select-none"
                        onClick={toggleAccordion}
                    >
                        <h3 className="font-medium text-gray-900">Activity Log</h3>
                        <FontAwesomeIcon
                            icon={isAccordionOpen ? faChevronUp : faChevronDown}
                            className="ml-2 h-4 w-4 text-indigo-900"
                        />
                    </div>
                </div>
                {isAccordionOpen && (
                    <div className="pl-6 mt-6">
                        {activityLogItems &&
                            (Array.isArray(activityLogItems)
                                ? activityLogItems.length > 0
                                : Object.keys(activityLogItems).length > 0) ? (
                            <ol className="relative border-s">
                                {(Array.isArray(activityLogItems)
                                    ? activityLogItems
                                    : [activityLogItems]
                                ).map((log, index) => (
                                    <li className="mb-6 flex items-center" key={index}>
                                        <span className="ml-[0.1rem] absolute flex items-center justify-center w-5 h-5 bg-[#02ADAD] rounded-full -start-3 ring-4 ring-white"></span>
                                        <div className="ml-6">
                                            <h3 className="mb-1 text-base font-semibold text-gray-900">
                                                {log.title}
                                            </h3>
                                            <time className="block mb-2 text-sm font-normal leading-none text-gray-400">
                                                {moment(log.timestamp).format('DD/MM/YYYY HH:mm:ss')}
                                            </time>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <ol className="relative border-s ">
                                <li className="mb-10 ms-6">
                                    <span className="absolute flex items-center justify-center w-5 h-5 bg-[#02ADAD] rounded-full -start-3 ring-4 ring-white"></span>
                                    <h3 className="flex items-center mb-1 text-base font-semibold text-gray-900">
                                        No Activity Log Data
                                    </h3>
                                    <time className="block mb-2 text-sm font-normal leading-none text-gray-400">
                                        {moment().format('DD/MM/YYYY HH:mm:ss')}
                                    </time>
                                </li>
                            </ol>
                        )}
                    </div>
                )}
            </div>

            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between">
                    <div
                        className="flex items-center cursor-pointer hover:text-indigo-600 transition-colors select-none"
                        onClick={toggleFormResponses}
                    >
                        <h3 className="font-medium text-gray-900">Form Responses</h3>
                        <FontAwesomeIcon
                            icon={isFormResponsesOpen ? faChevronUp : faChevronDown}
                            className="ml-2 h-4 w-4 text-indigo-900"
                        />
                    </div>
                </div>
                {isFormResponsesOpen && (
                    <div className="pl-6 mt-6 text-sm">
                        {requestItems?.v2 ? renderFormResponses() : renderLegacyFormResponses() }
                    </div>
                )}
            </div>

            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between">
                    <div
                        className="flex items-center cursor-pointer hover:text-indigo-600 transition-colors select-none"
                        onClick={toggleSessionHistory}
                    >
                        <h3 className="font-medium text-gray-900">Assessment History</h3>
                        <FontAwesomeIcon
                            icon={sessionHistoryOpen ? faChevronUp : faChevronDown}
                            className="ml-2 h-4 w-4 text-indigo-900"
                        />
                    </div>
                </div>
                {sessionHistoryOpen && (
                    <div className="pl-6 mt-6 text-sm">
                        {requestItems?.session_history?.length>0 ? (
                            <ol className="relative border-s">
                                {requestItems?.session_history?.map((session, index) => (
                                    <li className="mb-6 flex items-center" key={index}>
                                        <span className={`ml-[0.1rem] absolute flex items-center justify-center w-5 h-5 rounded-full -start-3 ring-4 ring-white ${session.cancelled ? 'bg-[#ad0202]' : 'bg-[#02ADAD]'}`}></span>
                                        <div className="ml-6">
                                            <div className="text-base font-semibold text-gray-900">
                                                {moment(session.start).format('dddd DD MMMM yyyy')} {session.cancelled ? (session.cancel_reason_id === 1 ? '(Did Not Attend)' : '(Cancelled)') : ''}
                                            </div>
                                            <time class="block mb-2 text-sm font-normal leading-none text-gray-400">
                                                {moment(session.start).format("HH:mm")} - {moment(session.end).format("HH:mm")}
                                            </time>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <div className='text-base font-semibold text-gray-900'>No Assessment History Found</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default OHViewRequest